import React, { useState, useEffect } from 'react';
import { Search, ClipboardCheck, MapPin, ChevronLeft, ChevronRight, CheckCircle, Info } from 'lucide-react';

export default function HowItWorks() {
    const [activeStep, setActiveStep] = useState(1);
    const [quizPage, setQuizPage] = useState(1);
    const [quizAnswers, setQuizAnswers] = useState({
        1: null,
        2: "Intermediate"
    });
    const [isAnimating, setIsAnimating] = useState(false);
    
    const totalQuizPages = 5;
    
    const handleNextStep = () => {
        if (activeStep < steps.length) {
            setIsAnimating(true);
            setTimeout(() => {
                setActiveStep(activeStep + 1);
                setIsAnimating(false);
            }, 300);
        }
    };
    
    const handlePrevStep = () => {
        if (activeStep > 1) {
            setIsAnimating(true);
            setTimeout(() => {
                setActiveStep(activeStep - 1);
                setIsAnimating(false);
            }, 300);
        }
    };
    
    const handleNextQuiz = () => {
        if (quizPage < totalQuizPages) {
            setQuizPage(quizPage + 1);
        } else {
            handleNextStep();
            setQuizPage(1);
        }
    };
    
    const handlePrevQuiz = () => {
        if (quizPage > 1) {
            setQuizPage(quizPage - 1);
        } else {
            handlePrevStep();
        }
    };
    
    const handleQuizAnswer = (question, answer) => {
        setQuizAnswers({
            ...quizAnswers,
            [question]: answer
        });
    };
    
    const steps = [
        {
            number: 1,
            title: "Enter Your Topic",
            description: "Simply input the subject you're interested in learning or making decisions about.",
            icon: Search,
            color: "from-blue-500 to-indigo-600"
        },
        {
            number: 2,
            title: "Take the Quiz",
            description: "Answer a few targeted questions to help us understand your current knowledge level and goals.",
            icon: ClipboardCheck,
            color: "from-purple-500 to-fuchsia-600"
        },
        {
            number: 3,
            title: "Get Your Roadmap",
            description: "Receive a customized path with clear steps to achieve mastery or make informed decisions.",
            icon: MapPin,
            color: "from-emerald-500 to-teal-600"
        }
    ];
    
    // Quiz Questions
    const quizQuestions = {
        1: {
            question: "Which of the following is a supervised learning algorithm?",
            options: [
                "K-Means Clustering",
                "Linear Regression",
                "Apriori Algorithm",
                "DBSCAN"
            ]
        },
        2: {
            question: "What is overfitting in Machine Learning?",
            options: [
                "A model that performs well on both training and test data",
                "A model that is too simple to capture patterns",
                "A model that performs well on training data but poorly on test data",
                "A model with high bias and low variance"
            ]
        },
        3: {
            question: "Which evaluation metric is most commonly used for classification problems?",
            options: [
                "Mean Squared Error (MSE)",
                "Root Mean Squared Error (RMSE)",
                "Accuracy",
                "R-squared"
            ]
        },
        4: {
            question: "Which activation function is commonly used in hidden layers of deep neural networks?",
            options: [
                "ReLU",
                "Softmax",
                "Sigmoid",
                "Linear"
            ]
        },
        5: {
            question: "Which technique is used to prevent overfitting in deep learning?",
            options: [
                "Gradient Descent",
                "Batch Normalization",
                "Dropout",
                "Data Augmentation"
            ]
        }
    };
    
    

    const stepContent = {
        1: (
            <div className="mockup-browser border border-base-300 w-full max-w-md lg:max-w-lg mx-auto shadow-xl bg-base-100 ">
                <div className="mockup-browser-toolbar">
                    <div className="input border border-base-300 px-1">pathfinder.app</div>
                </div>
                <div className="flex justify-center px-4 py-8 bg-base-200">
                    <div className="form-control w-full max-w-xs">
                        <label className="label">
                            <span className="label-text font-medium text-lg">What would you like to learn?</span>
                            <span className="label-text-alt">
                <Info size={16} className="text-info" />
              </span>
                        </label>
                        <input
                            type="text"
                            placeholder="e.g., Machine Learning"
                            className="input input-bordered input-primary w-full focus:ring-2 focus:ring-primary"
                        />
                        
                        
                        
                        <button
                            className="btn btn-primary mt-6 w-full group relative overflow-hidden"
                            onClick={handleNextStep}
                        >
                            <span className="relative z-10">Continue</span>
                            <span className="absolute right-4 top-1/2 transform -translate-y-1/2 transition-transform group-hover:translate-x-1">
                <ChevronRight size={18} />
              </span>
                        </button>
                    </div>
                </div>
            </div>
        ),
        2: (
            <div className="mockup-window border border-base-300 w-full max-w-md lg:max-w-lg mx-auto shadow-xl">
                <div className="px-5 py-8 bg-base-200 flex flex-col items-center">
                    <div className="text-center mb-6">
                        <div className="flex justify-center items-center mb-3">
                            <div className="badge badge-md sm:badge-sm lg:badge-lg badge-primary py-3 px-4 font-medium">
                                Question {quizPage} of {totalQuizPages}
                            </div>
                        </div>
                        <h3 className="font-bold text-xl">{quizQuestions[quizPage].question}</h3>
                    </div>
                    
                    <div className="flex flex-col w-full max-w-xs gap-3">
                        {quizQuestions[quizPage].options.map((option, index) => (
                            <button
                                key={index}
                                className={`btn ${quizAnswers[quizPage] === option ? 'btn-primary' : 'btn-outline btn-primary'} justify-between group hover:pl-6 transition-all duration-300`}
                                onClick={() => handleQuizAnswer(quizPage, option)}
                            >
                                {option}
                                {quizAnswers[quizPage] === option && (
                                    <CheckCircle size={18} className="opacity-100 transition-opacity duration-200" />
                                )}
                            </button>
                        ))}
                    </div>
                    
                    <div className="flex justify-between w-full max-w-xs mt-8">
                        <button
                            className="btn btn-outline btn-sm flex items-center gap-1 hover:gap-2 transition-all"
                            onClick={handlePrevQuiz}
                            disabled={quizPage === 1 && activeStep === 1}
                        >
                            <ChevronLeft size={16} />
                            Back
                        </button>
                        <div className="flex gap-1">
                            {Array.from({ length: totalQuizPages }).map((_, index) => (
                                <div
                                    key={index}
                                    className={`w-2 h-2 rounded-full ${quizPage === index + 1 ? 'bg-primary' : 'bg-base-300'}`}
                                />
                            ))}
                        </div>
                        <button
                            className="btn btn-primary btn-sm flex items-center gap-1 hover:gap-2 transition-all"
                            onClick={handleNextQuiz}
                            disabled={!quizAnswers[quizPage]}
                        >
                            Next
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            </div>
        ),
        3: (
            <div className="mockup-window border border-base-300 w-full max-w-md lg:max-w-lg mx-auto shadow-xl overflow-hidden bg-base-100">
                <div className="flex justify-between items-center px-6 py-3 bg-base-300">
                    <h3 className="font-bold">Your Custom Learning Roadmap</h3>
                    <div className="badge badge-primary  badge-md sm:badge-sm lg:badge-lg sm:text-[10px]">Machine Learning</div>
                </div>
                <div className="px-4 py-6 bg-base-200 overflow-auto max-h-[400px]">
                    
                    
                    <ul className="timeline timeline-vertical timeline-snap-icon">
                        <li>
                            <div className="timeline-middle">
                                <div className="avatar placeholder">
                                    <div className="bg-primary text-primary-content rounded-full w-10 relative">
                                        <span className="absolute top-2.5 left-4 font-bold">1</span>
                                    </div>
                                </div>
                            </div>
                            <div className="timeline-start md:text-end mb-10">
                                <div className="card bg-base-100 shadow-md">
                                    <div className="card-body p-4">
                                        <h3 className="card-title text-base font-bold">Fundamentals</h3>
                                        <p className="text-sm text-left">Basic concepts and mathematical foundations</p>
                                        <div className="flex gap-2 mt-1 flex-wrap">
                                            <div className="badge badge-primary badge-outline sm:text-[10px]">5 resources</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <hr className="bg-primary"/>
                        </li>
                        <li>
                            <hr className="bg-primary"/>
                            <div className="timeline-middle">
                                <div className="avatar placeholder">
                                    <div className="bg-primary text-primary-content rounded-full w-10">
                                        <span className="absolute top-2.5 left-4 font-bold">2</span>
                                    </div>
                                </div>
                            </div>
                            <div className="timeline-end mb-10">
                                <div className="card bg-base-100 shadow-md">
                                    <div className="card-body p-4">
                                        <h3 className="card-title text-base font-bold">Core Algorithms</h3>
                                        <p className="text-sm">Essential ML algorithms and techniques</p>
                                        <div className="flex gap-2 mt-1 flex-wrap">
                                            <div className="badge badge-primary badge-outline">8 resources</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <hr className="bg-primary"/>
                        </li>
                        <li>
                            <hr className="bg-primary"/>
                            <div className="timeline-middle">
                                <div className="avatar placeholder">
                                    <div className="bg-primary text-primary-content rounded-full w-10">
                                        <span className="absolute top-2.5 left-4 font-bold">3</span>
                                    </div>
                                </div>
                            </div>
                            <div className="timeline-start md:text-end mb-10">
                                <div className="card bg-base-100 shadow-md">
                                    <div className="card-body p-4">
                                        <h3 className="card-title text-base font-bold">Advanced Concepts</h3>
                                        <p className="text-sm">Deep learning and specialized techniques</p>
                                        <div className="flex gap-2 mt-1 flex-wrap">
                                            <div className="badge badge-outline">5 weeks</div>
                                            <div className="badge badge-primary badge-outline">6 resources</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <hr className="bg-primary"/>
                        </li>
                        <li>
                            <hr className="bg-primary"/>
                            <div className="timeline-middle">
                                <div className="avatar placeholder">
                                    <div className="bg-primary text-primary-content rounded-full w-10">
                                        <span className="absolute top-2.5 left-4 font-bold">4</span>
                                    </div>
                                </div>
                            </div>
                            <div className="timeline-end">
                                <div className="card bg-base-100 shadow-md">
                                    <div className="card-body p-4">
                                        <h3 className="card-title text-base font-bold">Applied Projects</h3>
                                        <p className="text-sm">Real-world applications and portfolio development</p>
                                        <div className="flex gap-2 mt-1 flex-wrap">
                                            <div className="badge badge-outline">4 weeks</div>
                                            <div className="badge badge-primary badge-outline">3 projects</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </li>
                    </ul>
                    
                    <div className="flex justify-center mt-6">
                        <button className="btn btn-primary">View Detailed Plan</button>
                    </div>
                </div>
            </div>
        )
    };
    
    useEffect(() => {
        
        return () => setIsAnimating(false);
    }, [activeStep]);
    
    return (
        <section id="how-it-works" className="py-16 px-4 sm:px-6 lg:px-8 bg-base-200 ">
        <div className="container mx-auto max-w-[1200px]">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-12 text-primary">
                    How PathFinder Works
                </h2>
                
                
                <div className="flex flex-wrap justify-center gap-4 md:gap-8 max-w-5xl mx-auto mb-12">
                    {steps.map((step) => {
                        const Icon = step.icon;
                        const isActive = activeStep === step.number;
                        const isPast = activeStep > step.number;
                        
                        return (
                            <button
                                key={step.number}
                                className={`
                  flex-1 min-w-[120px] sm:min-w-[150px] md:min-w-[200px] max-w-[300px] card justify-center items-center
                  ${isActive ? 'bg-primary text-primary-content shadow-lg' :
                                    isPast ? 'bg-base-300 border-2 border-primary/30' : 'bg-base-200 hover:bg-base-300'}
                  transition-all duration-300 p-4 sm:p-5 md:p-6 cursor-pointer transform
                  ${isActive ? 'scale-105' : 'scale-100'}
                `}
                                onClick={() => setActiveStep(step.number)}
                                disabled={isAnimating}
                            >
                                <div className="flex flex-col items-center text-center gap-2 sm:gap-3 md:gap-4">
                                    <div className={`
                    w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full flex justify-center items-center
                    ${isActive ? 'bg-primary-content/20 text-primary-content' :
                                        isPast ? `bg-primary/20 text-primary` :
                                            `bg-gradient-to-r ${step.color} text-white`}
                  `}>
                                        <Icon size={isActive || isPast ? 24 : 22} />
                                        {isPast && <CheckCircle className="absolute -right-1 -bottom-1 bg-base-100 rounded-full text-success" size={16} />}
                                    </div>
                                    <h3 className={`font-bold text-base sm:text-sm ${isPast ? 'text-primary/80' : ''}`}>
                                        {step.title}
                                    </h3>
                                </div>
                            </button>
                        );
                    })}
                </div>
                
                
                <div className="relative max-w-5xl mx-auto mb-12 hidden md:block">
                    <div className="absolute top-0 left-0 w-full h-2 bg-base-300 rounded-full"></div>
                    <div
                        className="absolute top-0 left-0 h-2 bg-gradient-to-r from-primary to-secondary transition-all duration-500 rounded-full"
                        style={{ width: `${((activeStep - 1) / (steps.length - 1)) * 100}%` }}
                    ></div>
                </div>
                
                
                <div className={`py-8 transition-all duration-300 ${isAnimating ? 'opacity-0 transform translate-y-4' : 'opacity-100 transform translate-y-0'}`}>
                    <div className="flex justify-center">
                        {stepContent[activeStep]}
                    </div>
                    <div className="text-center mt-8 max-w-2xl mx-auto">
                        <p className="text-lg sm:text-xl text-base-content/80">{steps[activeStep-1].description}</p>
                    </div>
                    
                    
                    <div className="flex justify-center items-center gap-4 mt-10">
                        <button
                            className={`btn btn-circle btn-outline ${activeStep === 1 ? 'btn-disabled opacity-50' : 'btn-primary'}`}
                            onClick={handlePrevStep}
                            disabled={activeStep === 1 || isAnimating}
                        >
                            <ChevronLeft size={24} />
                        </button>
                        
                        <div className="flex gap-2">
                            {steps.map((step, index) => (
                                <div
                                    key={index}
                                    className={`w-3 h-3 rounded-full cursor-pointer transition-all duration-300
                    ${activeStep === index + 1 ? 'bg-primary scale-125' :
                                        activeStep > index + 1 ? 'bg-primary/40' : 'bg-base-300'}`}
                                    onClick={() => setActiveStep(index + 1)}
                                />
                            ))}
                        </div>
                        
                        <button
                            className={`btn btn-circle ${activeStep === steps.length ? 'btn-disabled opacity-50' : 'btn-primary'}`}
                            onClick={handleNextStep}
                            disabled={activeStep === steps.length || isAnimating}
                        >
                            <ChevronRight size={24} />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}